import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { app } from '../../../src/admin/servicios/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

import {
  FaSearch,
  FaTicketAlt,
  FaDownload,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaBus,
  FaArrowRight,
  FaArrowLeft
} from 'react-icons/fa';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

export default function MisViajes() {
  const navigate = useNavigate();

  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(false);
  const [viajesActivos, setViajesActivos] = useState([]);
  const [viajesPasados, setViajesPasados] = useState([]);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [ticketSeleccionado, setTicketSeleccionado] = useState(null);
  const ticketRef = useRef(null);

  /* =========================
     BUSCAR VIAJES (CON CLOUD FUNCTION)
  ========================= */
  const buscarViajes = async (e) => {
    e.preventDefault();
    const dato = busqueda.trim();
    if (!dato) return;

    setLoading(true);
    setViajesActivos([]);
    setViajesPasados([]);
    setTicketSeleccionado(null);

    try {
      // 2. CONECTAMOS CON LA CLOUD FUNCTION EN LUGAR DE CONSULTAR LA BASE DE DATOS DIRECTO
      const functions = getFunctions(app, 'us-central1');
      const buscarTicketsFn = httpsCallable(functions, 'buscarMisTickets');

      const payload = { telefono: dato };

      // Llamamos al servidor
      const result = await buscarTicketsFn(payload);

      // La Cloud Function nos devuelve el arreglo de tickets en result.data
      const ticketsEncontrados = result.data || [];

      const hoy = new Date().toISOString().split('T')[0];
      const activos = [];
      const pasados = [];

      // 3. CLASIFICAMOS LOS TICKETS RECIBIDOS
      ticketsEncontrados.forEach((data) => {
        const fechaRef = data.tipoViaje === 'redondo'
          ? data.fechaRegreso || data.fechaViaje
          : data.fechaViaje;

        if (fechaRef >= hoy && data.estado !== 'cancelado') {
          activos.push(data);
        } else {
          pasados.push(data);
        }
      });

      // Opcional: Ordenarlos del más reciente al más antiguo
      activos.sort((a, b) => new Date(b.fechaCompra || 0) - new Date(a.fechaCompra || 0));
      pasados.sort((a, b) => new Date(b.fechaCompra || 0) - new Date(a.fechaCompra || 0));

      setViajesActivos(activos);
      setViajesPasados(pasados);

      if (activos.length === 0 && pasados.length === 0) {
        alert('No encontramos boletos con este dato.');
      }
    } catch (error) {
      console.error("Error al buscar boletos:", error);
      alert('Error al buscar. Revisa tu conexión y vuelve a intentar.');
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     DESCARGAR IMAGEN
  ========================= */
  const descargarImagen = async () => {
    if (ticketRef.current) {
      const canvas = await html2canvas(ticketRef.current, {
        backgroundColor: null
      });
      const link = document.createElement('a');
      link.download = `Boleto_${ticketSeleccionado.id.substring(0, 6)}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-slate-900 pb-20">
      <div className="max-w-3xl mx-auto">

        {/* ===== HEADER CON BOTÓN REGRESAR ===== */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="
              flex items-center gap-2 px-4 py-2 rounded-xl
              bg-blue-50 text-blue-700 font-bold
              border border-blue-200
              hover:bg-blue-100/70 hover:border-blue-300
              transition-all duration-200
              active:scale-95
            "
          >
            <FaArrowLeft />
            Regresar
          </button>

          <h1 className="text-2xl md:text-3xl font-black text-slate-800 uppercase flex items-center gap-3">
            <FaTicketAlt className="text-blue-600" /> Mis Viajes
          </h1>

          {/* Espaciador */}
          <div className="w-20" />
        </div>

        {/* ===== BUSCADOR ===== */}
        <div className="bg-white p-2 rounded-2xl shadow-lg mb-8 border border-slate-100 flex gap-2">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Teléfono con el que compraste"
            className="flex-1 px-4 py-3 bg-transparent outline-none text-lg font-medium text-slate-700 placeholder:text-slate-400"
          />
          <button
            onClick={buscarViajes}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 rounded-xl font-bold transition-all disabled:bg-slate-400"
          >
            {loading ? '...' : <FaSearch />}
          </button>
        </div>

        {/* ===== VIAJES ACTIVOS ===== */}
        <div className="space-y-4">
          {viajesActivos.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => setTicketSeleccionado(ticket)}
              className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 hover:shadow-md transition-all cursor-pointer relative overflow-hidden group"
            >
              <div
                className={`absolute top-0 left-0 w-2 h-full ${ticket.tipoViaje === 'redondo'
                    ? 'bg-purple-500'
                    : 'bg-emerald-500'
                  }`}
              />

              <div className="flex justify-between items-start mb-3 pl-3">
                <div>
                  <span
                    className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider ${ticket.tipoViaje === 'redondo'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-emerald-100 text-emerald-700'
                      }`}
                  >
                    {ticket.tipoViaje === 'redondo'
                      ? 'Redondo'
                      : 'Sencillo'}
                  </span>
                  <h3 className="font-bold text-lg text-slate-800 mt-1">
                    {ticket.tipoViaje === 'redondo'
                      ? (ticket?.fechaIda || ticket?.fechaViaje || 'Sin fecha')
                      : (ticket?.fechaViaje || 'Sin fecha')}
                  </h3>
                </div>

                <div className="text-right">
                  <p className="text-xs text-slate-400 font-bold uppercase">
                    Hora
                  </p>
                  <p className="text-xl font-black text-slate-800">
                    {ticket.tipoViaje === 'redondo'
                      ? (ticket?.horaIda || ticket?.horaViaje || '--:--')
                      : (ticket?.horaViaje || '--:--')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm text-slate-600 pl-3 border-t border-dashed border-slate-100 pt-3">
                <span className="font-bold truncate max-w-[40%]">
                  {ticket.tipoViaje === 'redondo'
                    ? (ticket?.rutaIda?.origen || ticket?.ruta?.origen || 'Origen N/A')
                    : (ticket?.ruta?.origen || 'Origen N/A')}
                </span>
                <FaArrowRight className="text-slate-300 text-xs flex-shrink-0" />
                <span className="font-bold truncate max-w-[40%]">
                  {ticket.tipoViaje === 'redondo'
                    ? (ticket?.rutaIda?.destino || ticket?.ruta?.destino || 'Destino N/A')
                    : (ticket?.ruta?.destino || 'Destino N/A')}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ===== HISTORIAL ===== */}
        {viajesPasados.length > 0 && (
          <div className="mt-8">
            <button
              onClick={() => setMostrarHistorial(!mostrarHistorial)}
              className="text-slate-400 font-bold text-sm flex items-center gap-2 mx-auto hover:text-slate-600"
            >
              {mostrarHistorial ? <FaChevronUp /> : <FaChevronDown />}
              {mostrarHistorial
                ? 'Ocultar historial'
                : `Ver ${viajesPasados.length} viajes anteriores`}
            </button>

            {mostrarHistorial && (
              <div className="mt-4 space-y-3 opacity-60">
                {viajesPasados.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => setTicketSeleccionado(ticket)}
                    className="bg-slate-100 p-4 rounded-xl flex justify-between items-center cursor-pointer hover:border-slate-300"
                  >
                    <div>
                      <p className="font-bold text-slate-700 text-sm">
                        {ticket.tipoViaje === 'redondo'
                          ? (ticket?.fechaIda || ticket?.fechaViaje || 'Sin fecha')
                          : (ticket?.fechaViaje || 'Sin fecha')}
                      </p>
                      <p className="text-xs text-slate-500 truncate w-48">
                        {ticket.tipoViaje === 'redondo'
                          ? (ticket?.rutaIda?.origen || ticket?.ruta?.origen || 'Origen N/A')
                          : (ticket?.ruta?.origen || 'Origen N/A')}
                        {' → '}
                        {ticket.tipoViaje === 'redondo'
                          ? (ticket?.rutaIda?.destino || ticket?.ruta?.destino || 'Destino N/A')
                          : (ticket?.ruta?.destino || 'Destino N/A')}
                      </p>
                    </div>
                    <span className="text-xs bg-slate-200 px-2 py-1 rounded font-bold text-slate-500">
                      Pasado
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== MODAL DETALLE ===== */}
        {ticketSeleccionado && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-100 w-full md:max-w-md md:rounded-3xl rounded-t-3xl overflow-hidden max-h-[90vh] flex flex-col shadow-2xl">

              <div className="bg-slate-900 p-4 flex justify-between items-center text-white">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <FaBus /> Detalles del Boleto
                </h3>
                <button
                  onClick={() => setTicketSeleccionado(null)}
                  className="bg-white/10 p-2 rounded-full"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="overflow-y-auto p-4">
                <div ref={ticketRef} className="bg-white rounded-2xl overflow-hidden shadow">

                  {/* HEADER */}
                  <div className={`p-5 text-white text-center ${ticketSeleccionado.tipoViaje === 'redondo' ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-blue-600'}`}>
                    <p className="font-black tracking-widest uppercase text-sm">Transportes Ángeles del Puerto</p>
                    <p className="text-blue-200 text-xs">{ticketSeleccionado.tipoViaje === 'redondo' ? 'Boleto Digital Redondo' : 'Boleto Digital'}</p>
                  </div>

                  <div className="p-5 space-y-4">

                    {ticketSeleccionado.tipoViaje === 'redondo' ? (
                      <>
                        {/* IDA */}
                        <div className="bg-blue-50 p-3 rounded-xl border-l-4 border-blue-500">
                          <p className="text-xs text-blue-600 font-black uppercase mb-2">🔵 Viaje de Ida</p>
                          <div className="flex justify-between items-center mb-2">
                            <div className="text-center">
                              <p className="text-[10px] text-gray-400 font-bold uppercase">Origen</p>
                              <p className="font-bold text-slate-800 text-sm">{ticketSeleccionado.rutaIda?.origen}</p>
                            </div>
                            <span className="text-gray-300">➔</span>
                            <div className="text-center">
                              <p className="text-[10px] text-gray-400 font-bold uppercase">Destino</p>
                              <p className="font-bold text-slate-800 text-sm">{ticketSeleccionado.rutaIda?.destino}</p>
                            </div>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="font-bold">📅 {ticketSeleccionado.fechaIda}</span>
                            <span className="font-bold">🕐 {ticketSeleccionado.horaIda}</span>
                          </div>
                        </div>

                        {/* REGRESO */}
                        <div className="bg-purple-50 p-3 rounded-xl border-l-4 border-purple-500">
                          <p className="text-xs text-purple-600 font-black uppercase mb-2">🟣 Viaje de Regreso</p>
                          <div className="flex justify-between items-center mb-2">
                            <div className="text-center">
                              <p className="text-[10px] text-gray-400 font-bold uppercase">Origen</p>
                              <p className="font-bold text-slate-800 text-sm">{ticketSeleccionado.rutaRegreso?.origen}</p>
                            </div>
                            <span className="text-gray-300">➔</span>
                            <div className="text-center">
                              <p className="text-[10px] text-gray-400 font-bold uppercase">Destino</p>
                              <p className="font-bold text-slate-800 text-sm">{ticketSeleccionado.rutaRegreso?.destino}</p>
                            </div>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="font-bold">📅 {ticketSeleccionado.fechaRegreso}</span>
                            <span className="font-bold">🕐 {ticketSeleccionado.horaRegreso}</span>
                          </div>
                        </div>

                        {/* PASAJEROS REDONDO */}
                        <div className="bg-slate-50 p-3 rounded-xl">
                          <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">Pasajeros y Asientos</p>
                          {ticketSeleccionado.asientosIda?.map((asientoIda, index) => (
                            <div key={asientoIda} className="flex justify-between text-sm mb-1 border-b border-gray-100 last:border-0 pb-1">
                              <span className="font-bold text-slate-700">{ticketSeleccionado.pasajeros?.[asientoIda]}</span>
                              <div className="flex gap-1">
                                <span className="bg-blue-100 text-blue-700 px-2 rounded font-bold text-xs">Ida: {asientoIda.replace('a', '')}</span>
                                <span className="bg-purple-100 text-purple-700 px-2 rounded font-bold text-xs">Reg: {ticketSeleccionado.asientosRegreso?.[index]?.replace('a', '')}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* SENCILLO */}
                        <div className="flex justify-between items-center border-b border-dashed border-gray-200 pb-4">
                          <div className="text-center">
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Origen</p>
                            <p className="font-bold text-slate-800">{ticketSeleccionado.ruta?.origen}</p>
                          </div>
                          <span className="text-gray-300">➔</span>
                          <div className="text-center">
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Destino</p>
                            <p className="font-bold text-slate-800">{ticketSeleccionado.ruta?.destino}</p>
                          </div>
                        </div>

                        <div className="flex justify-between bg-slate-50 p-3 rounded-xl">
                          <div>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Fecha</p>
                            <p className="font-bold text-sm">📅 {ticketSeleccionado.fechaViaje}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Hora</p>
                            <p className="font-bold text-sm">🕐 {ticketSeleccionado.horaViaje}</p>
                          </div>
                        </div>

                        <div className="bg-slate-50 p-3 rounded-xl">
                          <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">Pasajeros y Asientos</p>
                          {ticketSeleccionado.asientos?.map((asiento) => (
                            <div key={asiento} className="flex justify-between text-sm mb-1 border-b border-gray-100 last:border-0 pb-1">
                              <span className="font-bold text-slate-700">{ticketSeleccionado.pasajeros?.[asiento]}</span>
                              <span className="bg-blue-100 text-blue-700 px-2 rounded font-bold">Asiento {asiento.replace('a', '')}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* QR */}
                    <div className="flex flex-col items-center pt-2">
                      <div className="p-2 border-2 border-slate-900 rounded-lg">
                        <QRCode value={ticketSeleccionado.id} size={120} level="H" />
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">ID: {ticketSeleccionado.id}</p>
                      <p className="text-[10px] text-red-400 font-bold mt-1">Muestra este código al abordar</p>
                    </div>

                  </div>

                  <div className="bg-slate-100 p-3 text-center">
                    <p className="text-[10px] text-gray-500">Estado: <span className="text-green-600 font-bold uppercase">{ticketSeleccionado.estado}</span></p>
                  </div>

                </div>
              </div>

              <div className="p-4 bg-white border-t border-slate-100">
                <button
                  onClick={descargarImagen}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  <FaDownload /> Guardar Imagen
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}